import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { skip } from 'rxjs/operators';
import { NgxEchartsDirective } from 'ngx-echarts';
import type { EChartsOption } from 'echarts';
import { FinancialOverviewPoint } from '../../services/apis/dashboard/dashboard.service';
import { ThemeService } from '../../services/theme.service';
import { chartColors } from './chart-theme';

@Component({
  selector: 'app-margin-comparison-chart',
  standalone: true,
  imports: [NgxEchartsDirective],
  host: { class: 'block w-full h-full' },
  templateUrl: './margin-comparison-chart.component.html',
})
export class MarginComparisonChartComponent implements AfterViewInit, OnInit, OnChanges, OnDestroy {
  @Input() data: FinancialOverviewPoint[] | null = null;

  options: EChartsOption | null = null;
  readonly viewportSize = 12;
  viewportStart = 0;

  @ViewChild('chartContainer', { static: true }) private chartContainer?: ElementRef<HTMLElement>;

  private hasBuiltOptions = false;
  private themeSub?: Subscription;
  private chartInstance?: { resize: () => void };
  private resizeObserver?: ResizeObserver;
  private dataReference: FinancialOverviewPoint[] | null = null;
  private lastWheelTime = 0;
  private readonly themeService = inject(ThemeService);

  ngOnInit(): void {
    if (!this.hasBuiltOptions) {
      this.buildOptions();
    }
    this.themeSub = this.themeService.isDark$.pipe(skip(1)).subscribe(() => this.buildOptions());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && this.data !== this.dataReference) { this.dataReference = this.data; this.viewportStart = 0; }
    this.buildOptions();
  }

  ngAfterViewInit(): void {
    if (!this.chartContainer?.nativeElement) return;
    this.chartContainer.nativeElement.addEventListener('wheel', this.onNativeWheel, { passive: false, capture: true });
    this.resizeObserver = new ResizeObserver(() => this.chartInstance?.resize());
    this.resizeObserver.observe(this.chartContainer.nativeElement);
  }

  ngOnDestroy(): void {
    this.themeSub?.unsubscribe();
    this.resizeObserver?.disconnect();
    this.chartContainer?.nativeElement.removeEventListener('wheel', this.onNativeWheel, true);
  }

  onChartInit(instance: { resize: () => void }): void {
    this.chartInstance = instance;
    this.chartInstance.resize();
  }

  get hasScrollableViewport(): boolean { return (this.data?.length ?? 0) > this.viewportSize; }
  get canMoveOlder(): boolean { return this.viewportStart > 0; }
  get canMoveNewer(): boolean { return this.viewportStart < Math.max(0, (this.data?.length ?? 0) - this.viewportSize); }
  get viewportLabel(): string { const rows = this.visibleRows(); return rows.length ? `${rows[0].periodLabel || rows[0].period} – ${rows[rows.length - 1].periodLabel || rows[rows.length - 1].period}` : ''; }
  moveViewport(direction: 'older' | 'newer'): void {
    const max = Math.max(0, (this.data?.length ?? 0) - this.viewportSize);
    const next = Math.max(0, Math.min(max, this.viewportStart + (direction === 'newer' ? 1 : -1)));
    if (next === this.viewportStart) return; this.viewportStart = next; this.buildOptions();
  }
  private readonly onNativeWheel = (event: WheelEvent): void => {
    if (!this.hasScrollableViewport) return; event.preventDefault(); event.stopPropagation();
    const now = Date.now(); if (now - this.lastWheelTime < 120) return; this.lastWheelTime = now;
    this.moveViewport(event.deltaY > 0 ? 'newer' : 'older');
  };

  private buildOptions(): void {
    this.hasBuiltOptions = true;
    const rows = this.visibleRows();
    if (rows.length === 0) {
      this.options = null;
      return;
    }

    const c = chartColors();
    const labels = rows.map(row => row.periodLabel || row.period);
    const gpm = rows.map(row => this.grossProfitMargin(row));
    const npm = rows.map(row => this.netProfitMargin(row));

    this.options = {
      grid: { left: 34, right: 12, top: 14, bottom: 50, containLabel: true },
      tooltip: {
        trigger: 'axis',
        confine: true,
        axisPointer: { type: 'line', lineStyle: { color: c.cardBorder, type: 'dashed' } },
        backgroundColor: c.chartSurface,
        borderColor: c.cardBorder,
        borderWidth: 1,
        padding: [10, 12],
        textStyle: { color: c.textPrimary, fontSize: 12 },
        extraCssText: 'box-shadow: 0 12px 28px rgba(0,0,0,0.16); border-radius: 8px;',
        valueFormatter: value => `${Number(value).toFixed(1)}%`,
      },
      legend: {
        bottom: 0,
        itemGap: 16,
        icon: 'circle',
        data: ['GPM %', 'NPM %'],
        textStyle: { color: c.textSecondary, fontSize: 11, fontWeight: 500 },
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: labels,
        axisLine: { lineStyle: { color: c.cardBorder } },
        axisTick: { show: false },
        axisLabel: {
          color: c.textSecondary,
          fontSize: 11,
          margin: 12,
          interval: 0,
          rotate: 35,
        },
      },
      yAxis: {
        type: 'value',
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: c.textSecondary,
          fontSize: 11,
          formatter: (value: number) => `${value.toFixed(0)}%`,
        },
        splitLine: { lineStyle: { color: c.cardBorder, type: 'dashed', opacity: 0.75 } },
      },
      series: [
        this.lineSeries('GPM %', gpm, c.accent || c.chartSeries1),
        this.lineSeries('NPM %', npm, c.negative || c.chartSeries4),
      ],
    };
  }

  private lineSeries(name: string, data: number[], color: string) {
    const isSingleMonth = data.length === 1;
    return {
      name,
      type: isSingleMonth ? 'bar' as const : 'line' as const,
      data,
      barMaxWidth: 44,
      smooth: 0.2,
      symbol: 'circle',
      symbolSize: data.length <= 4 ? 9 : 6,
      itemStyle: {
        color,
        borderRadius: isSingleMonth
          ? ((data[0] ?? 0) < 0 ? [0, 0, 14, 14] : [14, 14, 0, 0])
          : 0,
      },
      lineStyle: { color, width: 2.6 },
      emphasis: { focus: 'series' as const },
    };
  }

  private grossProfitMargin(row: FinancialOverviewPoint): number {
    const value = Number(row.grossProfitMargin);
    return Number.isFinite(value) ? value : 0;
  }

  private netProfitMargin(row: FinancialOverviewPoint): number {
    const value = Number(row.netProfitMargin);
    return Number.isFinite(value) ? value : 0;
  }

  private visibleRows(): FinancialOverviewPoint[] {
    const rows = this.data ?? [];
    return this.hasScrollableViewport ? rows.slice(this.viewportStart, this.viewportStart + this.viewportSize) : rows;
  }
}
